import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Download, Calendar, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NewsletterView = () => {
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    fetchNewsletters();
  }, []);

  const fetchNewsletters = async () => {
    try {
      const response = await axios.get(`${API}/admin/newsletters`);
      setNewsletters(response.data);
    } catch (error) {
      toast.error('Failed to fetch newsletters');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (email) => {
    if (!window.confirm(`Are you sure you want to delete ${email}?`)) {
      return;
    }
    
    setDeleting(email);
    try {
      await axios.delete(`${API}/admin/newsletters/${encodeURIComponent(email)}`);
      toast.success(`${email} deleted successfully`);
      setNewsletters(newsletters.filter(n => n.email !== email));
    } catch (error) {
      toast.error('Failed to delete subscriber');
    } finally {
      setDeleting(null);
    }
  };

  const downloadCSV = () => {
    if (newsletters.length === 0) {
      toast.error('No data to download');
      return;
    }

    const headers = ['Email', 'Subscribed Date', 'Status'];
    const rows = newsletters.map(item => [
      item.email,
      new Date(item.subscribed_at).toLocaleString(),
      item.active ? 'Active' : 'Inactive'
    ]);
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `newsletter_subscribers_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('CSV downloaded successfully');
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="admin-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px' }}>
        <h2 className="section-subtitle">Newsletter Subscribers ({newsletters.length})</h2>
        <Button className="btn-primary" onClick={downloadCSV}>
          <Download size={18} style={{ marginRight: '8px' }} />
          Download CSV
        </Button>
      </div>

      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-color)' }}>
              <th style={{ padding: '15px', textAlign: 'left', color: 'var(--accent-silver)' }}>Email</th>
              <th style={{ padding: '15px', textAlign: 'left', color: 'var(--accent-silver)' }}>Subscribed Date</th>
              <th style={{ padding: '15px', textAlign: 'center', color: 'var(--accent-silver)' }}>Status</th>
              <th style={{ padding: '15px', textAlign: 'center', color: 'var(--accent-silver)' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {newsletters.length === 0 ? (
              <tr>
                <td colSpan="4" style={{ padding: '30px', textAlign: 'center', color: 'var(--text-muted)' }}>
                  No subscribers yet
                </td>
              </tr>
            ) : (
              newsletters.map((item) => (
                <tr key={item.id || item.email} style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <td style={{ padding: '15px', color: 'var(--text-primary)' }}>{item.email}</td>
                  <td style={{ padding: '15px', color: 'var(--text-secondary)' }}>
                    <Calendar size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                    {new Date(item.subscribed_at).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </td>
                  <td style={{ padding: '15px', textAlign: 'center' }}>
                    <span style={{ 
                      padding: '4px 12px', 
                      background: item.active !== false ? 'rgba(0, 255, 0, 0.1)' : 'rgba(255, 0, 0, 0.1)',
                      color: item.active !== false ? '#00ff00' : '#ff0000',
                      borderRadius: '12px',
                      fontSize: '0.85rem',
                      fontWeight: '600'
                    }}>
                      {item.active !== false ? 'Active' : 'Unsubscribed'}
                    </span>
                  </td>
                  <td style={{ padding: '15px', textAlign: 'center' }}>
                    <Button 
                      className="btn-secondary"
                      onClick={() => handleDelete(item.email)}
                      disabled={deleting === item.email}
                      style={{ 
                        padding: '6px 12px', 
                        background: 'transparent',
                        border: '1px solid var(--accent-red)',
                        color: 'var(--accent-red)'
                      }}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default NewsletterView;